import React, { useMemo, useState } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from "chart.js";
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { Product } from "../../../../interfaces/DTO/mislaka_xml_models";
import { buildBarDataset, buildChartOptions, ChartContainer } from "../../../../shared/UIParts/chartUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface ProductPremiumsStackedChartProps {
  product: Product;
}

type PeriodType = "yearly" | "monthly";

export const ProductPremiumsStackedChart: React.FC<ProductPremiumsStackedChartProps> = ({ product }) => {
  const [periodType, setPeriodType] = useState<PeriodType>("yearly");

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setPeriodType(event.target.value as PeriodType);
  };

  const chartData = useMemo(() => {
    const premiumsByYear: Record<string, Record<string, number>> = {}; // year -> coverage -> amount
    const yearsSet = new Set<number>();
    const lastKnownValue: Record<string, number> = {}; // coverage -> last known amount
    const yearRange: number[] = []; // לכל כיסוי נבדוק את סוף השנה מול התחלה
    let coverageInstanceCounter: Record<string, number> = {};

    const calculateMonths = (start: string, end: string): number => {
      if (!start || !end || start.length < 6 || end.length < 6) return 12;
      const sy = parseInt(start.slice(0, 4));
      const sm = parseInt(start.slice(4, 6));
      const ey = parseInt(end.slice(0, 4));
      const em = parseInt(end.slice(4, 6));
      return (ey - sy) * 12 + (em - sm) + 1;
    };

    product.policies_or_accounts.forEach((policy) => {
      policy.insurence_coverages?.forEach((coverage, idx) => {
        const baseName = coverage.coverage_name || `כיסוי ${coverage.coverage_number}`;
        coverageInstanceCounter[baseName] = (coverageInstanceCounter[baseName] || 0) + 1;
        const coverageName = `${baseName} #${coverageInstanceCounter[baseName]}`;

        coverage.premium_period?.forEach((period) => {
          const startYear = period.start_period?.slice(0, 4);
          const endYear = period.end_period?.slice(0, 4);
          const amount = period.premium_amount || 0;
          if (!startYear || !endYear || !period.start_period || !period.end_period) return;

          const start = parseInt(startYear);
          const end = parseInt(endYear);
          yearRange.push(end - start);

          const months = calculateMonths(period.start_period, period.end_period);
          let normalizedAmount = amount;

          if (months === 1) {
            normalizedAmount = periodType === "yearly" ? amount * 12 : amount;
          } else {
            normalizedAmount = periodType === "yearly" ? amount : amount / months;
          }

          normalizedAmount = Math.round(normalizedAmount);

          for (let year = start; year <= end; year++) {
            yearsSet.add(year);
            premiumsByYear[year] = premiumsByYear[year] || {};
            premiumsByYear[year][coverageName] = normalizedAmount;
            lastKnownValue[coverageName] = normalizedAmount;
          }
        });
      });
    });

    if (yearsSet.size === 0) return { labels: [], datasets: [] };

    const minYear = Math.min(...Array.from(yearsSet));
    const rawMaxYear = Math.max(...Array.from(yearsSet));
    const maxDetectedYear = yearRange.some(gap => gap > 40) ? Math.min(rawMaxYear, minYear + 19) : rawMaxYear;
    const allYears = Array.from({ length: maxDetectedYear - minYear + 1 }, (_, i) => (minYear + i).toString());

    const allCoverages = Array.from(new Set(Object.values(premiumsByYear).flatMap((c) => Object.keys(c))));

    const datasets = allCoverages.map((coverageName, index) => {
      let lastValue = lastKnownValue[coverageName] || 0;
      const data = allYears.map((yearStr) => {
        const value = premiumsByYear[parseInt(yearStr)]?.[coverageName];
        if (value !== undefined) {
          lastValue = value;
        }
        return lastValue;
      });
      const color = `hsl(${(index * 60) % 360}, 60%, 60%)`;

      return buildBarDataset({
        label: coverageName,
        data,
        backgroundColor: color,
        borderColor: color,
      });
    });

    return {
      labels: allYears,
      datasets,
    };
  }, [product, periodType]);

  const chartOptions = useMemo(() => buildChartOptions({
    isLineChart: false,
    isStacked: true,
    tooltip: {
      callbacks: {
        footer: (tooltipItems: TooltipItem<"bar">[]) => {
          const sum = tooltipItems.reduce((acc, item) => acc + (item.raw as number), 0);
          return `סך הכל: ${sum.toLocaleString()}`;
        },
      },
    },
  }), []);
  if (chartData?.labels?.length === 0) 
    return null;
  return (
    <Box>
      <FormControl size="small" sx={{ mb: 2, minWidth: 200,mt:2 }}>
        <InputLabel id="period-type-label">סוג תקופה</InputLabel>
        <Select
          labelId="period-type-label"
          value={periodType}
          onChange={handlePeriodChange}
          label="סוג תקופה"
        >
          <MenuItem value="yearly">שנתי</MenuItem>
          <MenuItem value="monthly">חודשי</MenuItem>
        </Select>
      </FormControl>
      <ChartContainer height={400}>
        <Chart type="bar" data={chartData} options={chartOptions} />
      </ChartContainer>
    </Box>
  );
};
